// L'accueil d'un membre sans le module Santé : le brief du jour (rappels et agendas connectés),
// les rappels à cocher, et ce qui attend une réponse. L'écran grand public complet viendra au lot 3.

import { useCallback, useEffect, useState } from 'react';

import type { ItemAgenda, Moi } from '@alfred/shared';
import type { Actions } from '../App';
import { Cloche, Fenetre, Icone, Nav } from '../components';
import { CarteQuestionDuJour } from './Entretien';
import { type Brief, type Demande, NonConnecte, type TableauEntretien, api } from '../lib/api';
import { appliquerAuBrief } from '../lib/fileActions';
import { jjmm } from '../lib/format';
import { BandeauInstallation } from './Installer';

function Ligne({ i, cocher }: { i: ItemAgenda; cocher?: () => void }) {
  return (
    <div className="alerte">
      <span className="t">{i.heure ? `${i.heure} · ` : ''}{i.titre}{i.detail ? ` (${i.detail})` : ''}</span>
      <span className="mono muted" style={{ fontSize: 11 }}>{jjmm(i.jour)}</span>
      {cocher && <button className="btn petit" onClick={cocher}>Fait</button>}
    </div>
  );
}

export function Accueil({ moi, actions }: { moi: Moi; actions: Actions }) {
  const [briefRecu, setBrief] = useState<Brief | null>(null);
  // Les rappels cochés hors ligne n'y sont plus, en attendant de partir (lot 6.5).
  const brief = briefRecu && appliquerAuBrief(briefRecu, actions.file);
  const [demandes, setDemandes] = useState<Demande[]>([]);
  const [entretien, setEntretien] = useState<TableauEntretien | null>(null);

  const charger = useCallback(async () => {
    try {
      const [b, d] = await Promise.all([api.brief(), api.demandes()]);
      setBrief(b);
      setDemandes(d);
      // L'entretien d'accueil : une invitation tant que les deux thèmes obligatoires ne sont pas faits.
      setEntretien(await api.entretien().catch(() => null));
    } catch (e) {
      if (e instanceof NonConnecte) window.location.hash = '#/connexion';
      else actions.toast({ texte: (e as Error).message, erreur: true });
    }
  }, [actions]);

  useEffect(() => {
    void charger();
  }, [charger]);

  const cocher = (i: ItemAgenda) => (i.rappel && i.rappelId ? () => void actions.rappelFait(i, `Fait : ${i.titre}`).then(charger) : undefined);

  return (
    <main className="ecran avec-nav">
      <div className="entete">
        <div className="marque"><span>Bonjour, {moi.membre.appellation ?? moi.membre.prenom}</span></div>
        <div style={{ display: 'flex' }}>
          {moi.modules.atelier && <a className="carre44" href="#/atelier" aria-label="Atelier"><Icone nom="atelier" /></a>}
          <Cloche />
          <a className="carre44" href="#/reglages" aria-label="Réglages"><Icone nom="reglages" /></a>
        </div>
      </div>

      {brief?.alertes.map((a) => <p key={a} className="bandeau-erreur" style={{ margin: 0 }}>{a}</p>)}
      <BandeauInstallation />

      {entretien && entretien.themes.some((t) => t.obligatoire && !t.fait) && (
        <a href="#/entretien" style={{ color: 'inherit' }}>
          <Fenetre>
            <p style={{ margin: 0, fontWeight: 600 }}>Faisons connaissance</p>
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>Trois minutes de questions, pour que {entretien.assistant} vous dérange au bon moment et vous parle comme vous l’aimez.</p>
          </Fenetre>
        </a>
      )}

      {entretien && !entretien.themes.some((t) => t.obligatoire && !t.fait) && <CarteQuestionDuJour actions={actions} />}

      {demandes.length > 0 && (
        <a href="#/a-valider" style={{ color: 'inherit' }}>
          <Fenetre><p className="message" style={{ margin: 0 }}>{demandes.length} proposition(s) à valider.</p></Fenetre>
        </a>
      )}

      {!brief && <p className="message muted">Chargement…</p>}
      {brief && (
        <>
          <div className="section"><span className="titre">Aujourd’hui</span></div>
          {brief.aujourdhui.length === 0 && <p className="muted">Rien de prévu aujourd’hui.</p>}
          {brief.aujourdhui.map((i, k) => <Ligne key={i.titre + k} i={i} cocher={cocher(i)} />)}
          {brief.enRetard.length > 0 && <div className="section"><span className="titre">En retard</span></div>}
          {brief.enRetard.map((i, k) => <Ligne key={i.titre + k} i={i} cocher={cocher(i)} />)}
          {brief.aVenir.length > 0 && <div className="section"><span className="titre">À venir</span></div>}
          {brief.aVenir.map((i, k) => <Ligne key={i.titre + k} i={i} cocher={cocher(i)} />)}
        </>
      )}
      <Nav actif="brief" sante={false} />
    </main>
  );
}
